import {Routes} from '@angular/router';

import {AuthGuard} from './_guards/auth.guard';
import {PermissionsGuard} from './_guards/permissions.guard';
import {FeatureGuard} from './_guards/feature.guard';

const appRoutes: Routes = [
  //
  // { path: '**', redirectTo: '', pathMatch: 'full' },

  // onboarding
  {
    path: 'onboarding',
    loadComponent: () => import('./pages/onboarding/onboarding.component').then(m => m.OnboardingComponent),
    data: {title: 'Inserisci i tuoi dati', isOnBoarding: true},
  },

  //  bootstrap
  {
    path: '',
    loadComponent: () => import('./pages/bootstrap/bootstrap.component').then(m => m.BootstrapComponent),
    data: {title: 'Loading...', isBootstrap: true},
  },

  // login
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
    canActivate: [AuthGuard],
    data: {title: 'Accedi', isHome: true},
  },

  // register
  {
    path: 'register',
    loadComponent: () => import('./register/register.component').then(m => m.RegisterComponent),
    canActivate: [AuthGuard],
    data: {title: 'Registrati', isHome: true},
  },

  //  home page
  {
    path: 'home',
    loadComponent: () => import('./pages/home/home.component').then(m => m.HomeComponent),
    canActivate: [AuthGuard],
    data: {title: 'Take better, faster, safer decisions', isHome: true, feedbackEnabled: true},
  },

  {
    path: 'projects',
    data: {title: 'Projects', feature: 'projects', redirect: '/decision/all'},
    canActivate: [AuthGuard, FeatureGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/projects/all-projects/projects.component').then(m => m.ProjectsComponent),
        data: {title: 'pages.home.component.check-decision-table'},
      },
      {
        path: ':project_id',
        data: {title: 'Project'},
        children: [
          {
            path: '',
            loadComponent: () => import('./pages/projects/project-detail/project-detail.component').then(m => m.ProjectDetailComponent),
          },
          {
            path: 'decision',
            loadComponent: () => import('./pages/decision-tables/decision-table/decision-table.component').then(m => m.DecisionTableComponent),
            data: {title: 'routing.title.open-decision-table'},
          },
          {
            path: 'statement-generations/:processId',
            loadComponent: () => import('./components/statement-generation-details/statement-generation-details.component').then(m => m.StatementGenerationDetailsComponent),
            data: {title: 'Dettagli Generazione Statement'},
          },
        ],
      },
    ],
  },

  {
    path: 'ckm-simulations',
    data: {title: 'Simulations', feature: 'ckm-simulator', redirect: '/'},
    canActivate: [AuthGuard, FeatureGuard],
    children: [
      {
        path: ':simulation-id',
        loadComponent: () => import('./components/ckm-simulations/ckm-simulation/ckm-simulation.component').then(m => m.CkmSimulationComponent),
      },
    ],
  },

  {
    path: 'ckm-maps',
    data: {title: 'Maps', feature: 'ckm-simulator', redirect: '/'},
    canActivate: [AuthGuard, FeatureGuard],
    children: [
      {
        path: ':map-id',
        loadComponent: () => import('./components/ckm-maps/ckm-map-details/ckm-map-details.component').then(m => m.CkmMapDetailsComponent),
      },
    ],
  },

  {
    path: 'surveys',
    data: {title: 'Surveys'},
    children: [
      {
        path: ':survey_id',
        data: {title: 'Survey'},
        children: [
          {
            path: '',
            loadComponent: () => import('./pages/surveys/survey-details-page/survey-details-page.component').then(m => m.SurveyDetailsPageComponent),
            data: {title: 'Survey', feature: 'surveys', redirect: '/'},
            canActivate: [AuthGuard, FeatureGuard],
          },
          {
            path: 'response',
            data: {title: 'Answer to survey'},
            loadComponent: () => import('./pages/surveys/survey-response-page/survey-response-page.component').then(m => m.SurveyResponsePageComponent),
          },
        ],
      },
    ],
  },

  {
    // Onboarding clinico (DBEK-391 epic, DBEK-405, ridisegno "candidato"): rotta
    // DECOUPLATA da 'surveys/:survey_id' - ancorata a candidate_id, che resta stabile
    // per tutto il ciclo di vita del candidato (registrazione, firma, importazione),
    // a differenza di survey_id che puo' churnare ad ogni salvataggio bozza (vedi
    // Candidate::currentSurvey() lato backend). Cosi' il link mandato al clinico il
    // giorno 1 della bozza resta valido identico anche a survey avviata/candidato
    // importato. A differenza di 'response' (rispondente anonimo), qui si raccolgono
    // dati fiscali/bancari di un clinico identificato, quindi richiede autenticazione.
    path: 'clinical-onboarding',
    data: {title: 'Clinical onboarding'},
    children: [
      {
        path: 'candidates/:candidate_id',
        data: {title: 'Clinical onboarding'},
        loadComponent: () => import('./pages/surveys/clinical-onboarding-wizard-page/clinical-onboarding-wizard-page.component').then(m => m.ClinicalOnboardingWizardPageComponent),
        canActivate: [AuthGuard],
      },
    ],
  },

  //  guest page
  {
    path: 'guest',
    loadComponent: () => import('./pages/guest/guest.component').then(m => m.GuestComponent),
    canActivate: [AuthGuard],
    data: {title: 'Take better, faster, safer decisions', isHome: true},
  },

  // error 404 page
  {
    path: 'error-page',
    loadComponent: () => import('./error-page/error-page.component').then(m => m.ErrorPageComponent),
    canActivate: [AuthGuard],
    data: {title: 'Errore 404', isErrorPage: true},
  },

  // file viewer page
  {
    path: 'file-viewer/:document_id',
    loadComponent: () => import('./components/fileviewer/file-viewer.component').then(m => m.FileViewerComponent),
    canActivate: [AuthGuard],
    data: {title: 'File Viewer'},
  },

  //  framework
  {
    path: 'framework',
    data: {title: ''},
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/frameworks/framework/framework.component').then(m => m.FrameworkComponent),
        data: {title: 'routing.title.framework-new'},
      },
      {
        path: 'all',
        loadComponent: () => import('./pages/frameworks/all-frameworks/all-frameworks.component').then(m => m.AllFrameworksComponent),
        data: {title: 'routing.title.framework-all'},
        canActivate: [AuthGuard],
        pathMatch: 'full',
      },
      {
        path: ':id_framework',
        loadComponent: () => import('./pages/frameworks/framework/framework.component').then(m => m.FrameworkComponent),
        data: {title: 'routing.title.framework'},
      },
    ],
  },

  //  tavolo decisionale
  {
    path: 'decision',
    data: {
      title: '',
      feature: 'projects',
      redirect: '/projects',
      featureIsNot: true,
    },
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/decision-tables/decision-table/decision-table.component').then(m => m.DecisionTableComponent),
        data: {title: 'routing.title.decision-table'},
      },
      {
        path: 'all',
        loadComponent: () => import('./pages/decision-tables/all-decision-table/all-decision-table.component').then(m => m.AllDecisionTableComponent),
        data: {title: 'routing.title.decision-table-all'},
        pathMatch: 'full',
      },
      {
        path: ':id_decision',
        data: {title: 'routing.title.decision-table-show'},
        children: [
          {path: '', redirectTo: 'show', pathMatch: 'full'},
          {
            path: 'show',
            loadComponent: () => import('./components/decision-table/show-decision-table/show-decision-table.component').then(m => m.ShowDecisionTableComponent),
            data: {title: 'routing.title.decision-table-show'},
          },
          {
            path: 'dashboard',
            loadComponent: () => import('./pages/decision-tables/decision-table-dashboard/decision-table-dashboard.component').then(m => m.DecisionTableDashboardComponent),
            data: {title: 'routing.title.decision-table-dashboard'},
          },
          {
            path: 'closing',
            loadComponent: () => import('./pages/decision-tables/decision-table-closing/decision-table-closing.component').then(m => m.DecisionTableClosingComponent),
            data: {title: 'routing.title.decision-table-closing'},
          },
          {
            path: 'task-management',
            loadComponent: () => import('./pages/task-management/task-management/task-management.component').then(m => m.TaskManagementComponent),
            data: {title: 'routing.title.decision-table-task-management'},
          },

        ],
      },
    ],
  },

  {
    path: 'decision/:id_decision/join-request',
    loadComponent: () => import('./pages/decision-join-request/decision-join-request.component').then(m => m.DecisionJoinRequestComponent),
    data: {
      title: 'routing.title.decision-join-request'
    },
  },

  //  risposte
  {
    path: 'responses',
    data: {title: ''},
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/responses/responses.component').then(m => m.ResponsesComponent),
        data: {title: 'routing.title.responses'},
      },
      {
        path: ':id_evaluation_session',
        data: {title: 'routing.title.responses-expert'},
        children: [
          {path: '', redirectTo: 'welcome', pathMatch: 'full'},
          {
            path: 'welcome',
            loadComponent: () => import('./pages/responses/responses-welcome/responses-welcome.component').then(m => m.ResponsesWelcomeComponent),
            data: {title: 'routing.title.responses-welcome'},
          },
          {
            path: 'explain',
            loadComponent: () => import('./pages/responses/responses-explain/responses-explain.component').then(m => m.ResponsesExplainComponent),
            data: {title: 'routing.title.responses-explain'},
          },
          {
            path: 'summary',
            loadComponent: () => import('./pages/responses/responses-summary/responses-summary.component').then(m => m.ResponsesSummaryComponent),
            data: {title: 'routing.title.responses-summary'},
          },
          {
            path: 'weight-target',
            loadComponent: () => import('./pages/responses/responses-weight-targets/responses-weight-targets.component').then(m => m.ResponsesWeightTargetsComponent),
            data: {title: 'routing.title.responses-weight-target'},
          },
          {
            path: 'weight-criteria',
            loadComponent: () => import('./pages/responses/responses-weight-criteria/responses-weight-criteria.component').then(m => m.ResponsesWeightCriteriaComponent),
            data: {title: 'routing.title.responses-weight-criteria'},
          },
          {
            path: 'responses-evaluation',
            loadComponent: () => import('./pages/responses/responses-evaluation/responses-evaluation.component').then(m => m.ResponsesEvaluationComponent),
            data: {title: 'routing.title.responses-evaluation'},
          },
          {
            path: 'suggest-proposal',
            loadComponent: () => import('./components/responses/responses-suggest-new-proposal/responses-suggest-new-proposal.component').then(m => m.ResponsesSuggestNewProposalComponent),
            data: {title: 'routing.title.responses-suggest-proposal'},
          },
          {
            path: 'suggest-proposal/:id_revision_proposal',
            loadComponent: () => import('./components/responses/responses-suggest-new-proposal/responses-suggest-new-proposal.component').then(m => m.ResponsesSuggestNewProposalComponent),
            data: {title: 'routing.title.responses-suggest-proposal'},
          },
          {
            path: 'thank-you',
            loadComponent: () => import('./pages/responses/responses-thank-you/responses-thank-you.component').then(m => m.ResponsesThankYouComponent),
            data: {title: 'routing.title.responses-thank-you'},
          },
        ],
      },
    ],
  },

  {
    path: 'contests',
    canActivate: [AuthGuard],
    data: {title: 'routing.title.responses-suggest-proposal'},
    children: [
      {
        path: 'propose/:token',
        loadComponent: () => import('./components/contests/new-contest-proposal-component/new-contest-proposal-component.component').then(m => m.NewContestProposalComponentComponent),
        data: {title: 'routing.title.responses-suggest-proposal'},
      },
    ],
  },

  // organizzazioni
  {
    path: 'organization',
    data: {title: ''},
    canActivate: [AuthGuard],
    children: [
      {
        path: 'all',
        loadComponent: () => import('./pages/organizations/organizations.component').then(m => m.OrganizationsComponent),
        canActivate: [AuthGuard],
        // data: { title: 'routing.title.organization-all' },
        data: {title: 'pages.organizations.component.your-organizations'},
        pathMatch: 'full',
      },
      {
        path: 'new',
        loadComponent: () => import('./pages/organizations/organization/organization.component').then(m => m.OrganizationComponent),
        canActivate: [AuthGuard],
        data: {title: 'routing.title.organization-new'},
        pathMatch: 'full',
      },
      {
        path: ':id_organization',
        data: {title: 'routing.title.organization'},
        children: [
          {
            path: '',
            loadComponent: () => import('./pages/organizations/organization-management/organization-management.component').then(m => m.OrganizationManagementComponent),
            canActivate: [AuthGuard],
            data: {title: 'routing.title.organization-manage'},
            pathMatch: 'full',
          },
          {
            path: 'show',
            loadComponent: () => import('./pages/organizations/organization/organization.component').then(m => m.OrganizationComponent),
            canActivate: [AuthGuard],
            data: {title: 'routing.title.organization-show'},
            pathMatch: 'full',
          },
          {
            path: 'project-properties',
            loadComponent: () => import('./pages/project-properties/project-property-list/project-property-list.component').then(m => m.ProjectPropertyListComponent),
            canActivate: [AuthGuard],
            data: {title: 'routing.title.project-properties'},
            pathMatch: 'full',
          },
          {
            path: 'role',
            data: {title: 'routing.title.role'},
            children: [
              {
                path: '',
                loadComponent: () => import('./pages/definition-roles/definition-roles.component').then(m => m.DefinitionRolesComponent),
                canActivate: [AuthGuard],
                data: {title: 'routing.title.organization-role-all'},
              },
              {
                path: 'new',
                loadComponent: () => import('./pages/organizations/organization-add-role/organization-add-role.component').then(m => m.OrganizationAddRoleComponent),
                canActivate: [AuthGuard],
                data: {title: 'routing.title.organization-role-new'},
                pathMatch: 'full',
              },
              {
                path: ':id_role',
                data: {title: 'routing.title.role-edit'},
                children: [
                  {
                    path: '',
                    loadComponent: () => import('./pages/organizations/organization-add-role/organization-add-role.component').then(m => m.OrganizationAddRoleComponent),
                    canActivate: [AuthGuard],
                    data: {title: 'routing.title.role-edit'},
                  },
                ],
              },
            ],
          },
          {
            path: 'user',
            data: {title: 'routing.title.user'},
            children: [
              {
                path: '',
                loadComponent: () => import('./pages/user/all-users-management/all-users-management.component').then(m => m.AllUsersManagementComponent),
                canActivate: [AuthGuard],
                data: {title: 'routing.title.user'},
              },
              {
                path: 'new/:id',
                loadComponent: () => import('./pages/user/single-user-management/single-user-management.component').then(m => m.SingleUserManagementComponent),
                canActivate: [AuthGuard],
                data: {title: 'routing.title.user-new'},
                pathMatch: 'full',
              },
              {
                path: ':id_user_organization',
                data: {title: 'routing.title.user-show'},
                children: [
                  {
                    path: '',
                    loadComponent: () => import('./pages/user/single-user-management/single-user-management.component').then(m => m.SingleUserManagementComponent),
                    canActivate: [AuthGuard],
                    data: {title: 'routing.title.user-show'},
                  },
                  {
                    path: 'curriculum/:id_curriculum',
                    loadComponent: () => import('./pages/user/curriculum/curriculum.component').then(m => m.CurriculumComponent),
                    canActivate: [AuthGuard],
                    data: {title: 'routing.title.user-curriculum'},
                    pathMatch: 'full',
                  },
                ],
              },
            ],
          },
          {
            path: 'documents',
            data: {title: 'routing.title.documents'},
            children: [
              {
                path: '',
                loadComponent: () => import('./pages/organizations/all-documents-management/all-documents-management.component').then(m => m.AllDocumentsManagementComponent),
                canActivate: [AuthGuard],
                data: {title: 'routing.title.documents'},
              },
            ],
          },
          {
            path: 'email-templates',
            data: {title: 'routing.title.email-templates'},
            children: [
              {
                path: '',
                loadComponent: () => import('./pages/organizations/template-mail-management/template-mail-management.component').then(m => m.TemplateMailManagementComponent),
                canActivate: [AuthGuard],
                data: {title: 'routing.title.email-templates'},
              },
            ],
          },
          {
            path: 'organization-charts',
            data: {title: 'routing.title.organization-charts'},
            children: [
              {
                path: '',
                loadComponent: () => import('./pages/organizations/organization-charts-management/organization-charts-management.component').then(m => m.OrganizationChartsManagementComponent),
                canActivate: [AuthGuard],
                data: {title: 'routing.title.organization-charts'},
              },
            ],
          },
        ],
      },
    ],
  },

  // User Notes
  {
    path: 'user-notes',
    loadComponent: () => import('./pages/user-notes/user-notes-list/user-notes-list.component').then(m => m.UserNotesListComponent),
    canActivate: [AuthGuard],
    data: {title: 'User Notes'},
  },

  //  TODO_MICHELE: organizzazioni da passare sia sotto la route organization che user con la differenziazione all'interno del component
  {
    path: 'organizational-functions',
    loadComponent: () => import('./pages/organizational-functions/organizational-functions.component').then(m => m.OrganizationalFunctionsComponent),
    canActivate: [AuthGuard],
    data: {title: 'Creare funzioni organizzative'},
  },
  {
    path: 'dmp',
    loadComponent: () => import('./pages/decision-management-plan/decision-management-plan/decision-management-plan.component').then(m => m.DecisionManagementPlanComponent),
    data: {title: 'Decision Management Plan'},
  },
  {
    path: 'profile',
    data: {title: 'Profilo'},
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/user/user-profile/user-profile.component').then(m => m.UserProfileComponent),
        canActivate: [AuthGuard],
        data: {title: 'Profilo'},
      },
      {
        path: 'change-password',
        loadComponent: () => import('./pages/user/change-password/change-password.component').then(m => m.ChangePasswordComponent),
        canActivate: [AuthGuard],
        data: {title: 'routing.title.change-password'},
        pathMatch: 'full',
      },
      {
        path: 'curriculum/:id_curriculum',
        loadComponent: () => import('./pages/user/curriculum/curriculum.component').then(m => m.CurriculumComponent),
        canActivate: [AuthGuard],
        data: {title: 'routing.title.user-curriculum'},
        pathMatch: 'full',
      },
    ],
  },
];

function addPermissionsGuardToRoutes(routes: any[]): any[] {
  return routes.map(route => {
    if (!route.children) {
      if (!route.redirectTo) {
        route.canActivate = route.canActivate || [];
        route.canActivate.push(PermissionsGuard);
      }
    }
    if (route.children) {
      route.children = addPermissionsGuardToRoutes(route.children);
    }
    return route;
  });
}

export const routes: Routes = addPermissionsGuardToRoutes(appRoutes);
